import { ElementType } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { CommunicationSearchItem, SpecificStatus } from '../../../hooks/Marketing';
import { Store } from '../../../types/Store';

export interface ChannelOption {
    id: number | null;
    label: string;
    icon: ElementType;
}

export const CHANNEL_OPTIONS: ChannelOption[] = [
    { id: 2, label: 'Email', icon: EmailIcon },
    { id: 4, label: 'SMS', icon: SmsIcon },
    { id: 8, label: 'Caisse', icon: PointOfSaleIcon },
    { id: 9, label: 'Wallet', icon: AccountBalanceWalletIcon },
    { id: null, label: 'Non précisé', icon: HelpOutlineIcon },
];

export const getChannelOption = (channelId: number | null | undefined): ChannelOption =>
    CHANNEL_OPTIONS.find((option) => option.id === (channelId ?? null)) ??
    CHANNEL_OPTIONS[CHANNEL_OPTIONS.length - 1];

export const SPECIFIC_STATUS_OPTIONS: Array<{ value: SpecificStatus; label: string }> = [
    { value: 'Draft', label: 'Brouillon' },
    { value: 'Waiting_validate', label: 'En attente de validation' },
];

// Statut de l'action une fois la communication prête à partir (correspond au filtre specificStatus=Waiting_validate)
export const ACTION_STATUS_WAITING_VALIDATION = 12;
export const ACTION_STATUS_TERMINATED = 21;
export const ACTION_STATUS_EXTRACTION_IN_PROGRESS = 8;
export const ACTION_STATUS_EXTRACTED = 10;
export const ACTION_STATUS_TEST = 99;
export const ACTION_STATUS_MODEL = 7;

export const ACTION_STATUS_OPTIONS: Array<{ value: number; label: string }> = [
    { value: ACTION_STATUS_EXTRACTED, label: 'Extraite' },
    { value: ACTION_STATUS_TERMINATED, label: 'Terminée' },
    { value: ACTION_STATUS_TEST, label: 'Test en attente de validation' },
    { value: ACTION_STATUS_MODEL, label: 'Modèle' },
];

// Statut "Récurrente" affiché dans la colonne Campagnes mais porté par le filtre specificStatus (cf. action_recurring)
export const RECURRING_STATUS_OPTION: { value: 'Recurring'; label: string } = {
    value: 'Recurring',
    label: 'Récurrente',
};

// Ces statuts d'action bloquent la suppression et la finalisation d'une communication
export const BLOCKING_ACTION_STATUS_IDS = [3, 4, 8, 10, 11, 21];

export const isActionStatusEditable = (actionStatusId: number | null): boolean =>
    actionStatusId == null || !BLOCKING_ACTION_STATUS_IDS.includes(actionStatusId);

// Une entité COMPANY/ROOT porte les communications nationales, les autres magasins les communications locales
export const isMainEntity = (entityType?: string): boolean => entityType === 'COMPANY' || entityType === 'ROOT';

export interface CommunicationStatusInfo {
    actionStatusId: number | null;
    isTerminated: boolean;
    isWaitingValidation: boolean;
    isExtractionInProgress: boolean;
    isExtracted: boolean;
    isTestOperation: boolean;
    isModel: boolean;
    remaining: RemainingTime | null;
    isExpired: boolean;
    canDelete: boolean;
    canFinalize: boolean;
    canView: boolean;
    canDuplicate: boolean;
}

export const getCommunicationStatusInfo = (
    data: Pick<CommunicationSearchItem, 'action' | 'validUntil'>
): CommunicationStatusInfo => {
    // Number(...) car l'API peut renvoyer actionStatusId sous forme de chaîne
    const actionStatusId = data.action?.actionStatusId != null ? Number(data.action.actionStatusId) : null;
    const isTerminated = actionStatusId === ACTION_STATUS_TERMINATED;
    const isWaitingValidation = actionStatusId === ACTION_STATUS_WAITING_VALIDATION;
    const isExtractionInProgress = actionStatusId === ACTION_STATUS_EXTRACTION_IN_PROGRESS;
    const isExtracted = actionStatusId === ACTION_STATUS_EXTRACTED;
    const isTestOperation = actionStatusId === ACTION_STATUS_TEST;
    const isModel = actionStatusId === ACTION_STATUS_MODEL;
    const remaining = isWaitingValidation ? getRemainingTime(data.validUntil) : null;
    const isExpired = remaining?.expired ?? false;
    const canDelete = !isTestOperation && isActionStatusEditable(actionStatusId);
    const canFinalize = !isTestOperation && isActionStatusEditable(actionStatusId);
    const canView = isTerminated || isExtracted || isTestOperation;
    const canDuplicate = isTerminated;

    return {
        actionStatusId,
        isTerminated,
        isWaitingValidation,
        isExtractionInProgress,
        isExtracted,
        isTestOperation,
        isModel,
        remaining,
        isExpired,
        canDelete,
        canFinalize,
        canView,
        canDuplicate,
    };
};

const findStoreByEntityId = (entityId: number | null, stores: Store[] | null): Store | undefined => {
    if (entityId == null || !stores) return undefined;
    return stores.find((store) => String(store.ENTITY_ID) === String(entityId));
};

export const isLocalCommunication = (entityId: number | null, stores: Store[] | null): boolean => {
    if (entityId == null) return false;
    const store = findStoreByEntityId(entityId, stores);
    return !store || !isMainEntity(store.ENTITY_TYPE);
};

export const getCommunicationType = (entityId: number | null, stores: Store[] | null): string =>
    isLocalCommunication(entityId, stores) ? 'Campagne locale' : 'Campagne nationale';

export const getStoreLabel = (entityId: number | null, stores: Store[] | null): string => {
    if (entityId == null) return '—';
    const store = findStoreByEntityId(entityId, stores);
    if (!store) return `#${entityId}`;
    return store.IDENTIFIER ? `${store.IDENTIFIER} - ${store.NAME}` : store.NAME;
};

// Parsing manuel (pas de new Date().toLocaleDateString) pour ne pas dépendre de l'ICU du navigateur
// ni convertir le fuseau horaire — on affiche l'heure telle que renvoyée par l'API, ex: 2026-09-04T14:26:51+02:00
const ISO_DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

export const formatDateTime = (isoDate: string | null): string => {
    if (!isoDate) return '—';
    const match = ISO_DATE_TIME_RE.exec(isoDate);
    if (!match) return '—';
    const [, year, month, day, hours, minutes] = match;
    return `${day}/${month}/${year} à ${hours}:${minutes}`;
};

export interface RemainingTime {
    expired: boolean;
    label: string;
}

// Une communication sans date limite renseignée est considérée comme expirée (rien ne garantit plus son envoi)
export const getRemainingTime = (isoDate: string | null): RemainingTime => {
    if (!isoDate) return { expired: true, label: 'Expirée' };

    const target = new Date(isoDate);
    if (Number.isNaN(target.getTime())) return { expired: true, label: 'Expirée' };

    const diffMs = target.getTime() - Date.now();
    if (diffMs <= 0) return { expired: true, label: 'Expirée' };

    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}j`);
    if (days > 0 || hours > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}min`);

    return { expired: false, label: `${parts.join(' ')} restantes` };
};
